/**
 * RegistryEntryCard — renders any registry entry type with conditional sections
 * based on registryType. Single component for all registry types.
 */

import React from 'react';
import { ConfigSchemaView } from './ConfigSchemaView';
import { WiringSection } from './WiringSection';
import { debugTheme } from './debugTheme';
import type { ActionTypeWire } from '../../api/wiring.schema';
import type {
  HandlerEntry,
  InteractionEntry,
  ArtifactCardEntry,
  LayoutEntry,
  ViewContextEntry,
} from '../../../../../src/shared/view-system/registries';

interface BaseProps {
  theme?: 'dark' | 'light';
  expanded?: boolean;
  onToggle?: () => void;
}

type RegistryEntryCardProps =
  | (BaseProps & { entry: ActionTypeWire; registryType: 'actionType' })
  | (BaseProps & { entry: HandlerEntry; registryType: 'handler' })
  | (BaseProps & { entry: InteractionEntry; registryType: 'interaction' })
  | (BaseProps & { entry: ArtifactCardEntry; registryType: 'artifactCard' })
  | (BaseProps & { entry: LayoutEntry; registryType: 'viewType' })
  | (BaseProps & { entry: ViewContextEntry; registryType: 'viewContext' });

const themes = {
  dark: {
    card: `${debugTheme.dark.bgAlt} border ${debugTheme.dark.border} rounded-lg`,
    header: debugTheme.dark.bgAlt,
    idBadge: `${debugTheme.dark.bg} ${debugTheme.dark.textAccent} font-mono text-[11px] px-2 py-0.5 rounded`,
    label: `${debugTheme.dark.text} text-sm font-medium`,
    categoryBadge: `${debugTheme.dark.badge} rounded-full px-2 py-0.5 text-xs font-medium`,
    description: `${debugTheme.dark.textMuted} text-xs`,
    kvKey: debugTheme.dark.textMuted,
    kvValue: debugTheme.dark.text,
    boolTrue: debugTheme.dark.boolTrue,
    boolFalse: debugTheme.dark.boolFalse,
    pill: debugTheme.dark.pill,
    separator: debugTheme.dark.border,
    chevron: debugTheme.dark.chevron,
  },
  light: {
    card: `${debugTheme.light.bg} border ${debugTheme.light.border} rounded-lg`,
    header: debugTheme.light.bgAlt,
    idBadge: `bg-slate-100 ${debugTheme.light.text} font-mono text-[11px] px-2 py-0.5 rounded`,
    label: `${debugTheme.light.text} text-sm font-medium`,
    categoryBadge: `${debugTheme.light.badge} rounded-full px-2 py-0.5 text-xs font-medium`,
    description: `${debugTheme.light.textMuted} text-xs`,
    kvKey: debugTheme.light.textMuted,
    kvValue: debugTheme.light.text,
    boolTrue: debugTheme.light.boolTrue,
    boolFalse: debugTheme.light.boolFalse,
    pill: debugTheme.light.pill,
    separator: debugTheme.light.border,
    chevron: debugTheme.light.chevron,
  },
} as const;

type CardTheme = (typeof themes)[keyof typeof themes];

function KVRow({
  label,
  value,
  t,
}: {
  label: string;
  value: React.ReactNode;
  t: CardTheme;
}) {
  return (
    <div className="flex gap-2 text-[11px] font-mono">
      <span className={`${t.kvKey} min-w-[130px] shrink-0`}>{label}:</span>
      <span className={t.kvValue}>{value}</span>
    </div>
  );
}

function BoolValue({ value, t }: { value: boolean; t: CardTheme }) {
  return <span className={value ? t.boolTrue : t.boolFalse}>{value ? 'true' : 'false'}</span>;
}

function PillList({ items, t }: { items: string[]; t: CardTheme }) {
  if (items.length === 0) return <span className={t.boolFalse}>none</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item) => (
        <span key={item} className={t.pill}>
          {item}
        </span>
      ))}
    </div>
  );
}

function ActionTypeFields({
  entry,
  t,
  theme,
}: {
  entry: ActionTypeWire;
  t: CardTheme;
  theme: 'dark' | 'light';
}) {
  return (
    <>
      <KVRow label="defaultInteraction" value={entry.defaultInteraction} t={t} />
      <KVRow label="isDecision" value={<BoolValue value={entry.isDecision} t={t} />} t={t} />
      <KVRow label="executionLocation" value={entry.executionLocation} t={t} />
      {entry.defaultOutcome && (
        <KVRow label="defaultOutcome" value={entry.defaultOutcome.status} t={t} />
      )}
      {entry.suggestedHandler && (
        <KVRow label="suggestedHandler" value={entry.suggestedHandler} t={t} />
      )}
      {entry.requiredDataFields && entry.requiredDataFields.length > 0 && (
        <WiringSection
          title="requiredDataFields"
          count={entry.requiredDataFields.length}
          theme={theme}
        >
          <div className="space-y-0.5">
            {entry.requiredDataFields.map((f) => (
              <div key={f.key} className="text-[11px] font-mono">
                <span className={t.kvValue}>{f.key}</span>
                <span className={t.kvKey}> : {f.type}</span>
              </div>
            ))}
          </div>
        </WiringSection>
      )}
      {entry.interactionPresets && entry.interactionPresets.length > 0 && (
        <WiringSection title="interactionPresets" count={entry.interactionPresets.length} theme={theme}>
          <div className="space-y-2">
            {entry.interactionPresets.map((p) => (
              <div key={p.id} className="space-y-0.5">
                <div className="text-[11px] font-mono">
                  <span className={t.kvValue}>{p.id}</span>
                  <span className={t.kvKey}> — {p.label}</span>
                </div>
                <div className={`text-[10px] ${t.kvKey}`}>{p.description}</div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  <span className={`${t.pill} text-[10px] px-1.5 py-0.5 rounded`}>mode: {p.interactionMode}</span>
                  {p.inputFields?.map((f) => (
                    <span key={f.key} className={`${t.pill} text-[10px] font-mono px-1.5 py-0.5 rounded`}>{f.key}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </WiringSection>
      )}
    </>
  );
}

function HandlerFields({
  entry,
  t,
  theme,
}: {
  entry: HandlerEntry;
  t: CardTheme;
  theme: 'dark' | 'light';
}) {
  return (
    <>
      {entry.suggestedInteraction && (
        <KVRow label="suggestedInteraction" value={entry.suggestedInteraction} t={t} />
      )}
      {entry.configSchema.length > 0 && (
        <WiringSection title="configSchema" count={entry.configSchema.length} theme={theme}>
          <ConfigSchemaView fields={entry.configSchema} theme={theme} />
        </WiringSection>
      )}
      {/* recipes removed from HandlerEntry */}
      {entry.requirements && entry.requirements.length > 0 && (
        <WiringSection title="requirements" count={entry.requirements.length} theme={theme}>
          <div className="space-y-1">
            {entry.requirements.map((r) => (
              <div key={r.id} className="text-[11px] font-mono">
                <span className={t.kvValue}>{r.label}</span>
                {r.description && <span className={t.kvKey}> — {r.description}</span>}
              </div>
            ))}
          </div>
        </WiringSection>
      )}
    </>
  );
}

function InteractionFields({
  entry,
  theme,
}: {
  entry: InteractionEntry;
  theme: 'dark' | 'light';
}) {
  if (entry.configSchema.length === 0) return null;
  return (
    <WiringSection title="configSchema" count={entry.configSchema.length} theme={theme}>
      <ConfigSchemaView fields={entry.configSchema} theme={theme} />
    </WiringSection>
  );
}

function ArtifactCardFields({ entry, t }: { entry: ArtifactCardEntry; t: CardTheme }) {
  const previewDesc =
    entry.preview.type === 'none'
      ? `none — ${entry.preview.reason}`
      : entry.preview.type === 'template_card'
        ? `template_card (actions: ${entry.preview.showActions}, fields: ${entry.preview.showFields})`
        : `field_layout (${entry.preview.layout}, emphasis: ${entry.preview.emphasis})`;

  return (
    <>
      <KVRow label="supportsActions" value={<BoolValue value={entry.supportsActions} t={t} />} t={t} />
      <KVRow label="preview" value={previewDesc} t={t} />
    </>
  );
}

function ViewTypeFields({ entry, t }: { entry: LayoutEntry; t: CardTheme }) {
  return <KVRow label="icon" value={entry.icon} t={t} />;
}

function ViewContextFields({ entry, t }: { entry: ViewContextEntry; t: CardTheme }) {
  return (
    <>
      <KVRow label="defaultViewType" value={entry.defaultViewType} t={t} />
      <div className="pt-1">
        <div className={`text-[11px] font-mono ${t.kvKey} mb-1`}>allowedViewTypes:</div>
        <PillList items={entry.allowedViewTypes} t={t} />
      </div>
    </>
  );
}

function renderFields(props: RegistryEntryCardProps, t: CardTheme, theme: 'dark' | 'light') {
  switch (props.registryType) {
    case 'actionType':
      return <ActionTypeFields entry={props.entry} t={t} theme={theme} />;
    case 'handler':
      return <HandlerFields entry={props.entry} t={t} theme={theme} />;
    case 'interaction':
      return <InteractionFields entry={props.entry} theme={theme} />;
    case 'artifactCard':
      return <ArtifactCardFields entry={props.entry} t={t} />;
    case 'viewType':
      return <ViewTypeFields entry={props.entry} t={t} />;
    case 'viewContext':
      return <ViewContextFields entry={props.entry} t={t} />;
  }
}

function getCategoryLabel(props: RegistryEntryCardProps): string | null {
  if (props.registryType === 'actionType') return props.entry.category;
  if (props.registryType === 'handler') return props.entry.category;
  return null;
}

export function RegistryEntryCard(props: RegistryEntryCardProps) {
  const { entry, theme = 'dark', expanded = false, onToggle } = props;
  const t = themes[theme];
  const categoryLabel = getCategoryLabel(props);

  return (
    <div className={t.card}>
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center gap-2 px-3 py-2 cursor-pointer select-none ${t.header} rounded-t-lg`}
      >
        <span
          className={`inline-block transition-transform duration-150 text-[10px] ${t.chevron} ${expanded ? 'rotate-90' : ''}`}
        >
          ▶
        </span>
        <span className={t.idBadge}>{entry.id}</span>
        <span className={t.label}>{entry.label}</span>
        {categoryLabel && <span className={t.categoryBadge}>{categoryLabel}</span>}
        {'icon' in entry && entry.icon && (
          <span className="ml-auto text-sm">{entry.icon}</span>
        )}
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className={`px-3 py-2 border-t ${t.separator} space-y-1.5`}>
          {/* Description */}
          {'description' in entry && entry.description && (
            <div className={t.description}>{entry.description}</div>
          )}

          {/* Conditional sections — narrowed by discriminated union via switch */}
          {renderFields(props, t, theme)}
        </div>
      )}
    </div>
  );
}
